/**
 * Dr. Mani's Magic Quiz — brand config.
 * Verbatim from dr-mm-files/dmm-quiz-spec.json -> brand.
 * Product URLs, the offer (code, guarantee, free-shipping, rewards), voice rules.
 */
import type { AddOnKey, ProductKey } from './types';

export const DMM_PRODUCTS: Record<ProductKey, { name: string; url: string; role: string }> = {
  plant_super_boost: {
    name: 'Plant Super Boost',
    url: 'https://drmanismagic.com/products/plant-super-boost',
    role: 'LIFE',
  },
  crab_kelp_amino_acids: {
    name: 'Crab, Kelp & Amino Acids',
    url: 'https://drmanismagic.com/products/crab-kelp-amino-acids',
    role: 'FOOD',
  },
  super_soil: {
    name: 'Super Soil',
    url: 'https://drmanismagic.com/products/super-soil',
    role: 'HOME',
  },
  the_bundle: {
    name: "The Dr. Mani's Magic Bundle",
    url: 'https://drmanismagic.com/pages/bundle-builder',
    role: 'ALL',
  },
};

export const DMM_LINKS = {
  bundle: 'https://drmanismagic.com/pages/bundle-builder',
  field_guide: 'https://drive.google.com/file/d/1QrXpa5xgsrfYdXRuBI7hAoPFQm_ltvis/view',
  reviews: 'https://drmanismagic.com/pages/reviews',
  rewards: 'https://drmanismagic.com/pages/drmanis-rewards',
  terms: 'https://drmanismagic.com/policies/terms-of-service',
  privacy: 'https://drmanismagic.com/policies/privacy-policy',
} as const;

export const DMM_OFFER = {
  quiz_code: 'QUIZ10',
  quiz_code_stacks: true,
  free_shipping_threshold: '$75',
  guarantee: '60-day keep-it',
  deadline: 'Your code closes with the last piece of your emailed reading.',
  rewards: 'Give $17 / Get $17, join free +$5',
} as const;

export const DMM_VOICE = {
  reading_level: '3rd grade',
  no_em_dashes: true,
  no_exclamation_points: true,
  format: 'about 80% one sentence per line',
  emphasis:
    'bold = product names/codes/numbers; italic = guarantee + best punches; underline = free shipping over $75 + urgency; never stack 3',
  central_truth: 'You never had a brown thumb. The soil and tools were the problem, not the gardener.',
  three_things: 'Plant Super Boost = LIFE, Crab Kelp & Amino Acids = FOOD, Super Soil = HOME',
  made_in:
    'made by hand on a family farm in Hargill, Texas (pop 461), USA materials except coco coir; not on Amazon or in big box stores',
} as const;

export const DMM_REVIEWS_NOTE =
  'Use only real, verified, 5-star reviews from the DMM export. One per results page / email, never invented, never repeated, no citrus reviews.';

/**
 * Hardcoded fallback product metadata (image + aggregate reviews/rating) used
 * when /api/shopify-sync/product-metadata returns nothing for drmanismagic.
 * Why this matters: keeps the results product cards from rendering blank when
 * the sync endpoint is cold or unavailable. Values mirror the live Shopify CDN.
 */
export const DMM_PRODUCT_FALLBACKS: Record<
  string,
  { imageUrl: string; reviews: number; rating: number }
> = {
  'plant-super-boost': {
    imageUrl:
      'https://cdn.shopify.com/s/files/1/0573/0097/5798/files/Plant_Super_Boost_-_Hero_Image_Props_USA.jpg?v=1779968843',
    reviews: 734,
    rating: 4.94,
  },
  'crab-kelp-amino-acids': {
    imageUrl:
      'https://cdn.shopify.com/s/files/1/0573/0097/5798/files/Organic_Fertilizer_-_Hero_Image_Props_USA.jpg?v=1779969066',
    reviews: 48,
    rating: 4.96,
  },
  'super-soil': {
    imageUrl:
      'https://cdn.shopify.com/s/files/1/0573/0097/5798/files/Super_Soil_-_Hero_Image_Props_USA.jpg?v=1779968734',
    reviews: 831,
    rating: 4.95,
  },
};

/**
 * Supplementary add-on products, keyed by AddOnKey.
 *
 * The spec marks these as "no Shopify URL" because they are recommended extras
 * (some, like the Chameleon Sprayer, are not made by Dr. Mani). The user asked
 * for them to render as real, shoppable product cards, so each entry carries a
 * Shopify handle (for the live product-metadata sync / image) and a product URL
 * (for the CTA). Handles + URLs below were verified against the live
 * drmanismagic.com catalog (every /products/<handle> returns 200) on 2026-06-27.
 */
export const DMM_ADDON_PRODUCTS: Record<
  AddOnKey,
  { name: string; handle: string; url: string }
> = {
  rice_hulls: {
    name: 'Rice Hulls',
    handle: 'organic-rice-hulls',
    url: 'https://drmanismagic.com/products/organic-rice-hulls',
  },
  coco_coir: {
    name: 'Coco Coir',
    handle: 'coco-block',
    url: 'https://drmanismagic.com/products/coco-block',
  },
  biochar: {
    name: 'Biochar',
    handle: 'organic-biochar-bioactive-carbon-soil-health-moisture-retention',
    url: 'https://drmanismagic.com/products/organic-biochar-bioactive-carbon-soil-health-moisture-retention',
  },
  chameleon_sprayer: {
    name: 'Chameleon Sprayer',
    handle: 'chameleon-hose-end-sprayer',
    url: 'https://drmanismagic.com/products/chameleon-hose-end-sprayer',
  },
  diatomaceous_earth: {
    name: 'Diatomaceous Earth',
    handle: 'organic-diatomaceous-earth',
    url: 'https://drmanismagic.com/products/organic-diatomaceous-earth',
  },
  sulfur_dust: {
    name: 'Sulfur Dust',
    handle: 'micronized-sulfur-dust',
    url: 'https://drmanismagic.com/products/micronized-sulfur-dust',
  },
  castile_soap: {
    name: 'Castile Soap',
    handle: 'castile-soap',
    url: 'https://drmanismagic.com/products/castile-soap',
  },
};

/**
 * Hardcoded fallback image + review counts for the supplementary add-ons.
 *
 * Why this matters: the live /api/shopify-sync/product-metadata sync does not
 * always return the add-on handles (they are secondary products), so without a
 * fallback the "Also Recommended" cards render with a blank gradient placeholder.
 * Image URLs are the og:image of each live PDP (verified 200 on 2026-06-27).
 * Review counts are real, taken from the live /collections/all listing.
 * Ratings are intentionally null — the store has no structured aggregateRating
 * (reviews are served by a third-party app), and the spec forbids invented
 * ratings ("Use only real, verified reviews… never invented"). The card only
 * shows stars/rating when a real rating exists.
 */
export const DMM_ADDON_FALLBACKS: Record<
  string,
  { imageUrl: string; reviews: number | null; rating: number | null }
> = {
  'organic-rice-hulls': {
    imageUrl: 'https://drmanismagic.com/cdn/shop/files/Ricehulls_product_photo.jpg?v=1779871658',
    reviews: 83,
    rating: null,
  },
  'coco-block': {
    imageUrl: 'https://drmanismagic.com/cdn/shop/files/coco_product_photo.webp?v=1779117385',
    reviews: 17,
    rating: null,
  },
  'organic-biochar-bioactive-carbon-soil-health-moisture-retention': {
    imageUrl: 'https://drmanismagic.com/cdn/shop/files/biochar_product_photo.jpg?v=1779872339',
    reviews: 24,
    rating: null,
  },
  'chameleon-hose-end-sprayer': {
    imageUrl: 'https://drmanismagic.com/cdn/shop/files/71LqeKTp-lL._AC_SL1500_1_4.jpg?v=1780630449',
    reviews: null,
    rating: null,
  },
  'organic-diatomaceous-earth': {
    imageUrl:
      'https://cdn.shopify.com/s/files/1/0573/0097/5798/files/fertilome_diatomaceous_earth.png?v=1756809772',
    reviews: 1,
    rating: null,
  },
  'micronized-sulfur-dust': {
    imageUrl:
      'https://cdn.shopify.com/s/files/1/0573/0097/5798/files/micronized_sulfur_dust.png?v=1756809649',
    reviews: 1,
    rating: null,
  },
  'castile-soap': {
    imageUrl: 'https://drmanismagic.com/cdn/shop/files/castilesoap.png?v=1780405335',
    reviews: null,
    rating: null,
  },
};
